import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Type, Hash, ToggleLeft } from 'lucide-react';
import Icon from '../../../shared/components/Icon';
import {
  getTransactionProperties,
  getUserPropertiesForTransaction,
  getDefaultOperator,
  getOperatorsForTransactionType,
} from '../config/transactionFilterProperties';

function getDataTypeIcon(type) {
  switch (type) {
    case 'string': return Type;
    case 'number': return Hash;
    case 'datetime': return ({ color = "currentColor", size = 18 }) => <Icon name="calendar" size={size} color={color} />;
    case 'boolean': return ToggleLeft;
    default: return Type;
  }
}

/**
 * Per-contract transaction filter: choose Transaction or User property, then add filter.
 * Same pattern as EventPropertyFilterModule but for transaction + user props only.
 */
const TransactionPropertyFilterModule = ({
  isOpen,
  onClose,
  triggerRef,
  contractId,
  contractName,
  teamId,
  filters = [],
  onFiltersChange,
  existingFilter = null,
  isGlobalFilter = false,
  onGlobalFilterAdd = null,
  firstTimeOnly = false,
  onFirstTimeChange = null,
}) => {
  const [selectedNavItem, setSelectedNavItem] = useState('transaction');
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const popupRef = useRef(null);

  const transactionProperties = getTransactionProperties();
  const userProperties = getUserPropertiesForTransaction();

  useEffect(() => {
    if (!isOpen) return;

    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;

    // Popup dimensions
    const popupWidth = 400; // Actual width from style
    const popupHeight = 360; // Approximate height (maxHeight: 360px)
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 16; // Minimum margin from viewport edges

    let top;
    let left;

    if (triggerRef?.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();

      // Calculate initial position (below trigger, aligned to left)
      top = triggerRect.bottom + scrollY + 8;
      left = triggerRect.left + scrollX;

      // Check if popup would overflow right edge
      if (left + popupWidth > viewportWidth - margin) {
        // Position to the left of trigger instead
        left = triggerRect.right + scrollX - popupWidth;
        // If still overflows, align to right edge with margin
        if (left < margin) {
          left = viewportWidth - popupWidth - margin;
        }
      }

      // Check if popup would overflow left edge
      if (left < margin) {
        left = margin;
      }

      // Check if popup would overflow bottom edge
      if (top + popupHeight > viewportHeight + scrollY - margin) {
        // Position above trigger instead
        top = triggerRect.top + scrollY - popupHeight - 8;
        // If still overflows, align to top with margin
        if (top < scrollY + margin) {
          top = scrollY + margin;
        }
      }
    } else {
      // Fallback: center the popup in the viewport
      left = scrollX + (viewportWidth - popupWidth) / 2;
      top = scrollY + (viewportHeight - popupHeight) / 2;
    }

    // Ensure popup never goes above top margin
    if (top < scrollY + margin) {
      top = scrollY + margin;
    }

    setPosition({ top, left });
  }, [isOpen, triggerRef]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isOpen &&
        popupRef.current &&
        !popupRef.current.contains(e.target) &&
        triggerRef?.current &&
        !triggerRef.current.contains(e.target)
      ) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose, triggerRef]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const propsList = selectedNavItem === 'transaction' ? transactionProperties : userProperties;
  const filteredProps = propsList.filter(
    (p) =>
      (p.label || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddPropertyFilter = (property) => {
    const field = property.name;
    const operator = getDefaultOperator(property.type);
    const didChangeProperty = existingFilter ? (existingFilter.property !== field) : true;

    if (isGlobalFilter && typeof onGlobalFilterAdd === 'function') {
      const newFilter = {
        id: existingFilter?.id || `global-tx-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        property: field,
        propertyLabel: property.label,
        propertyType: property.type,
        propertySource: property.source,
        field,
        // Keep operator only when it still makes sense for the new property type,
        // but ALWAYS reset values when the property changes (prevents "2 selected" leaking across selectors).
        operator: existingFilter && property.type === existingFilter.propertyType ? existingFilter.operator : operator,
        value: existingFilter && !didChangeProperty && property.type === existingFilter.propertyType ? existingFilter.value : null,
        value2: existingFilter && !didChangeProperty && property.type === existingFilter.propertyType ? existingFilter.value2 : null,
        connector: existingFilter?.connector,
      };
      onGlobalFilterAdd(newFilter);
      onClose();
      return;
    }

    if (existingFilter) {
      const updated = {
        ...existingFilter,
        property: field,
        propertyLabel: property.label,
        propertyType: property.type,
        propertySource: property.source,
        // Same rationale as global filters: keep operator when type matches, but reset value(s) on property change
        operator: property.type === existingFilter.propertyType ? existingFilter.operator : operator,
        value: !didChangeProperty && property.type === existingFilter.propertyType ? existingFilter.value : null,
        value2: !didChangeProperty && property.type === existingFilter.propertyType ? existingFilter.value2 : null,
        contractId: existingFilter.contractId || contractId,
        contractName: existingFilter.contractName || contractName,
      };
      onFiltersChange(updated);
    } else {
      const newFilter = {
        id: `tx-prop-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        contractId,
        contractName,
        property: field,
        propertyLabel: property.label,
        propertyType: property.type,
        propertySource: property.source,
        field: field,
        operator,
        value: null,
        value2: null,
      };
      onFiltersChange(newFilter);
    }
    onClose();
  };

  return createPortal(
    <div
      ref={popupRef}
      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl flex flex-col overflow-hidden"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: '400px',
        maxHeight: '360px',
        fontFamily: 'Raleway, sans-serif',
      }}
    >
      <div className="px-3 py-2 border-b border-gray-200 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#475569]" />
          <input
            type="text"
            placeholder="Search properties..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1d0c46]"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#475569]"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="w-28 border-r border-gray-200 bg-gray-50 flex-shrink-0 py-2">
          {[
            { id: 'transaction', label: 'Transaction' },
            { id: 'users', label: 'Users' },
          ].map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setSelectedNavItem(item.id)}
              className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                selectedNavItem === item.id
                  ? 'bg-white border-l-2 border-[#4b57aa] text-[#4b57aa] font-semibold'
                  : 'text-[#475569] hover:bg-gray-100'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-2">
          {filteredProps.length === 0 ? (
            <div className="p-4 text-sm text-[#475569] text-center">
              {searchTerm ? 'No properties match.' : 'No properties.'}
            </div>
          ) : (
            <div className="space-y-0.5">
              {filteredProps.map((prop, idx) => {
                const Icon = getDataTypeIcon(prop.type);
                const isSelected = existingFilter && existingFilter.property === prop.name && existingFilter.propertySource === prop.source;
                return (
                  <button
                    key={`${prop.source}-${prop.name}-${idx}`}
                    type="button"
                    onClick={() => handleAddPropertyFilter(prop)}
                    className={`w-full text-left px-3 py-2 text-sm rounded transition-colors flex items-center gap-2 ${
                      isSelected ? 'bg-gray-200 border border-gray-300' : 'hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="h-4 w-4 text-[#475569] flex-shrink-0" />
                    <span className="flex-1">{prop.label}</span>
                    <span
                      className="text-[10px] font-semibold uppercase w-5 h-5 inline-flex items-center justify-center rounded bg-gray-200 text-[#475569]"
                      title={prop.source === 'user' ? 'User' : 'Transaction'}
                    >
                      {prop.source === 'user' ? 'U' : 'T'}
                    </span>
                    {isSelected && <span className="text-xs text-[#475569]">(current)</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {typeof onFirstTimeChange === 'function' && (
        <div className="px-3 py-2 border-t border-gray-200 flex-shrink-0 flex items-center gap-2">
          <input
            type="checkbox"
            id="tx-first-time-only"
            checked={!!firstTimeOnly}
            onChange={(e) => onFirstTimeChange(e.target.checked)}
            className="rounded border-gray-300"
          />
          <label htmlFor="tx-first-time-only" className="text-sm text-[#475569] cursor-pointer">
            First time only
          </label>
        </div>
      )}
    </div>,
    document.body
  );
};

export default TransactionPropertyFilterModule;
export { getOperatorsForTransactionType, getDataTypeIcon };
